import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  FormControlLabel,
  Switch,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Chat } from '@mui/icons-material';
import {
  getApiErrorMessage,
  useApiGet,
  useApiSend,
} from '../../utils/httpClientTs';
import {
  getChapterChatStatus,
  joinChapterChat,
  leaveChapterChat,
} from '../../endpoints/messagesTs';
import { ChapterChatStatus } from '../../types/messages';

type Props = {
  localChapterId: string;
};

export default function ChapterChatOptIn({ localChapterId }: Props) {
  const {
    data: status,
    isFetching,
    refetch,
    isError,
    error,
  } = useApiGet<ChapterChatStatus>(
    ['chapter-chat', localChapterId],
    () => getChapterChatStatus(localChapterId),
    { enabled: !!localChapterId, retry: 1 }
  );

  const { mutate: join, isPending: joining } = useApiSend<
    ChapterChatStatus,
    string
  >(
    joinChapterChat,
    () => {
      void refetch();
    },
    undefined,
    [['chapter-chat', localChapterId], ['conversations']]
  );

  const { mutate: leave, isPending: leaving } = useApiSend<
    { success: boolean },
    string
  >(
    leaveChapterChat,
    () => {
      void refetch();
    },
    undefined,
    [['chapter-chat', localChapterId], ['conversations']]
  );

  const busy = joining || leaving || isFetching;
  const joined = !!status?.joined;

  return (
    <Card elevation={2} sx={{ mt: 3 }}>
      <CardHeader title="Chapter chat" avatar={<Chat color="primary" />} />
      <CardContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Opt in to your local chapter group conversation. You can mute
          notifications later from the conversation without leaving.
        </Typography>
        {isError && (
          <Typography color="error" variant="body2" sx={{ mb: 1 }}>
            {getApiErrorMessage(error)}
          </Typography>
        )}
        {isFetching && !status ? (
          <CircularProgress size={24} />
        ) : (
          <FormControlLabel
            control={
              <Switch
                checked={joined}
                disabled={busy || !localChapterId}
                onChange={(_, checked) => {
                  if (checked) {
                    join(localChapterId);
                  } else {
                    leave(localChapterId);
                  }
                }}
              />
            }
            label={joined ? 'Joined chapter chat' : 'Join chapter chat'}
          />
        )}
      </CardContent>
    </Card>
  );
}
